/* eslint-disable camelcase */
/* eslint-disable no-undefined */
/* eslint-disable no-shadow */

import type { BookingState, BookingService, TicketTariff } from './types'
import {
  BookingStateManager,
  getBookingServicesCatalog,
  hasServicesStep,
  resolveAvailablePaymentTimings,
  resolveFullPaymentAmount
} from './state'
import { postJson } from 'application/utils/csrf-request'
import { renderWithCurrency } from 'application/utils/currency'
import { CounterField } from 'application/ui/fields/counter-field'
import { SelectField, SelectType } from 'application/ui/fields/select'
import { TextField } from 'application/ui/fields/text'
import { BookingValidator } from './validation'

interface TariffPrice {
  value: string
  label: string
  price: number
  players: number
}

// Тарифы слота с tariff_url: none — тарифы не нужны (нет tariff_url или
// билетный слот), slow — повтор, в котором сервер ждёт партнёра дольше,
// empty — партнёр ответил, что тарифов нет.
type TariffsStatus = 'none' | 'loading' | 'slow' | 'ready' | 'empty' | 'failed'

const TARIFFS_STATUS_TEXT: Partial<Record<TariffsStatus, string>> = {
  loading: 'Загружаем тарифы…',
  slow: 'Уточняем актуальные цены, ещё немного…',
  empty: 'На это время онлайн-бронирование недоступно. Выберите другое время.',
  failed: 'Не удалось загрузить тарифы.'
}

// Пауза перед повтором: первый запрос к партнёру бывает долгим, а следующий
// за ним проходит быстро.
const TARIFFS_RETRY_DELAY = 1500

// Сервер ответил 503: партнёр не отдал тарифы и прошлого ответа нет.
class TariffsUnavailableError extends Error {}

/**
 * Класс для обновления UI элементов модалки бронирования
 */
export class BookingUIUpdater {
  private modal: HTMLElement
  private stateManager: BookingStateManager
  private currencyUnit: string
  private ticketsCounter: CounterField | null
  private tariffsCache: Map<string, TariffPrice[]>
  private tariffsLoading: Set<string>
  private tariffsFailed: Set<string>
  private tariffsSlow: Set<string>

  constructor(modal: HTMLElement, stateManager: BookingStateManager) {
    this.modal = modal
    this.stateManager = stateManager
    this.currencyUnit = this.getCurrencyUnit()
    this.ticketsCounter = null
    this.tariffsCache = new Map()
    this.tariffsLoading = new Set()
    this.tariffsFailed = new Set()
    this.tariffsSlow = new Set()
  }

  private getCurrencyUnit(): string {
    const currencyMeta = document.querySelector('meta[name="app:currency"]')
    const currency = currencyMeta?.getAttribute('content') || ''

    return currency.trim()
  }

  /**
   * Вывести сумму со знаком валюты. Знак — отдельный span.currency-sign
   * (как в QuestPageHelper#format_price): у Stolzl нет глифа «₽», без своего
   * шрифта браузер подставлял его из шрифта с засечками.
   * @param {HTMLElement} element - куда выводить
   * @param {string} amount - уже отформатированная сумма (с префиксом «+ »/«− »)
   * @param {string} prefix - текст перед суммой («~ »)
   * @param {string} suffix - текст после знака валюты (« за игрока»)
   */
  private renderPrice(
    element: HTMLElement,
    amount: string,
    prefix: string = '',
    suffix: string = ''
  ): void {
    const nodes: Node[] = [document.createTextNode(`${prefix}${amount}`)]

    if (this.currencyUnit) {
      const sign = document.createElement('span')
      sign.className = 'currency-sign'
      sign.textContent = this.currencyUnit
      nodes.push(document.createTextNode('\u00A0'), sign)
    }

    if (suffix) nodes.push(document.createTextNode(suffix))

    element.replaceChildren(...nodes)
  }

  /**
   * Обновить все элементы UI
   */
  updateAll(): void {
    const state = this.stateManager.getState()
    // Итог по сертификату (списано/остаток или причина отказа) есть и на
    // экране «Спасибо», и на экране оплаты — рендерим из state во все блоки.
    this.modal
      .querySelectorAll<HTMLElement>('[data-certificate-payment-summary]')
      .forEach((summary) => {
        renderWithCurrency(summary, state.certificateSummary || '')
        summary.hidden = !state.certificateSummary
      })

    // Показать нужный шаг
    this.updateSteps(state)

    // Отрендерить поле количества игроков, если оно нужно для текущего режима
    this.updatePlayersCountVisibility(state)

    // Обновить количество игроков
    this.updatePlayersCount(state)

    // Обновить билеты
    this.updateTickets(state)

    // Обновить информацию о квесте
    this.updateQuestInfo(this.modal, this.stateManager.getState())

    // Обновить тарифы
    this.updateTariffs(state)

    // Обновить языки
    this.updateLanguageOptions(state)

    // Обновить способы оплаты
    this.updatePaymentMethods(state)

    // Промокод/сертификат: на шаге 2 у квестов с услугами, иначе на шаге 1
    this.placeDiscounts(state)

    // «Далее» или сразу «Забронировать» на шаге 1
    this.updateStepOneButton(state)

    // Обновить дополнительные услуги
    this.updateAdditionalServices(state)

    // Обновить билеты билетной системы (Quest#ticket_system)
    this.updateTicketVisitDate(state)
    this.updateTicketTariffs(state)

    // Обновить итоговую сумму
    this.updatePrice()

    // Обновить таймер для closeStep 2
    const closeTimer = this.modal.querySelector(
      '[data-close-timer]'
    ) as HTMLElement
    if (closeTimer) {
      closeTimer.style.display = state.closeStep === 2 ? 'block' : 'none'
    }
  }

  /**
   * Показать нужный шаг
   * @param {BookingState} state - состояние бронирования
   */
  private updateSteps(state: BookingState): void {
    const bookingHeader = this.modal.querySelector(
      '[data-quest-info].booking-modal__header'
    ) as HTMLElement | null
    if (bookingHeader) {
      bookingHeader.hidden = state.step === 'confirm-phone' || state.isClosing
      bookingHeader.style.setProperty(
        '--booking-progress',
        this.getStepProgress(state)
      )

      const progress = bookingHeader.querySelector(
        '[data-booking-progress]'
      ) as HTMLElement | null

      if (progress) {
        progress.hidden =
          state.step !== 1 && state.step !== 2 && state.step !== 3
      }
    }

    const steps = this.modal.querySelectorAll('[data-booking-step]')
    steps.forEach((step) => {
      const stepValue = step.getAttribute('data-booking-step')
      const isFormStep =
        stepValue === '1' || stepValue === '2' || stepValue === '3'
      const shouldShow = state.isClosing
        ? stepValue === 'close'
        : stepValue === String(state.step)

      step.classList.toggle('is-hidden', !shouldShow)
      step.classList.toggle('is-active', shouldShow)

      if (isFormStep && stepValue !== String(state.step)) {
        const submitButton = step.querySelector(
          'button[type="submit"]'
        ) as HTMLButtonElement | null

        if (submitButton) {
          submitButton.disabled = true
        }
      }
    })
  }

  /**
   * Полоса прогресса: шаги до подтверждения брони. Без доп.услуг их два
   * (форма → «Квест забронирован»): 50% и 100%; с услугами три: 33%, 66%, 100%.
   * Оплата на последнем экране на полосу не влияет.
   * @param {BookingState} state - состояние бронирования
   * @return {string} ширина заполненной части в процентах
   */
  private getStepProgress(state: BookingState): string {
    const totalSteps = hasServicesStep(state) ? 3 : 2
    let currentStep = 1

    if (state.step === 2) currentStep = 2
    if (state.step === 3 || state.step === 'success') currentStep = totalSteps

    return `${Math.round((currentStep / totalSteps) * 100)}%`
  }

  /**
   * Обновить информацию о квесте
   * @param {HTMLElement | null} container - контейнер
   * @param {BookingState} state - состояние бронирования
   */
  public updateQuestInfo(
    container: HTMLElement | null,
    state: BookingState
  ): void {
    if (!state.quest || !state.slot || !container) return

    const elements = {
      image: container.querySelector('[data-quest-image]') as HTMLImageElement,
      name: container.querySelector('[data-quest-name]'),
      metro: container.querySelector('[data-quest-metro]'),
      gamersCount: container.querySelector('[data-quest-gamers-count]'),
      length: container.querySelector('[data-quest-length]'),
      age: container.querySelector('[data-quest-age]'),
      dateTime: container.querySelector('[data-date-time]')
    }

    const dateTimeRaw = [state.slot.date, state.slot.time]
      .filter(Boolean)
      .join(', ')
    const dateTime = state.quest.ticket_system
      ? this.formatTicketVisitDate(state.slot.date)
      : this.formatSlotDateTime(state.slot.date, state.slot.time, dateTimeRaw)

    if (elements.image) elements.image.src = state.quest.quest_photo
    if (elements.name) elements.name.textContent = state.quest.quest_name
    if (elements.metro) elements.metro.textContent = `${state.quest.metro}`
    if (elements.gamersCount)
      elements.gamersCount.textContent = this.getPlayersText(state)
    if (elements.length) elements.length.textContent = state.quest.time_text
    if (elements.age) elements.age.textContent = state.quest.age_text
    if (elements.dateTime) {
      elements.dateTime.textContent = dateTime

      const isoDateTime = state.quest.ticket_system
        ? state.slot.date
        : [state.slot.date, state.slot.time].filter(Boolean).join('T')

      if (isoDateTime) {
        elements.dateTime.setAttribute('datetime', isoDateTime)
      }
    }
  }

  /**
   * Билет действует весь день — в шапке только дата посещения, без времени.
   * @param {string} date - выбранный день (YYYY-MM-DD) или пустая строка
   * @return {string} «12 октября, понедельник» или подсказка выбрать дату
   */
  private formatTicketVisitDate(date: string): string {
    const parsedDate = date ? new Date(`${date}T00:00`) : null
    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
      return 'Выберите дату посещения'
    }

    const formattedDate = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long'
    }).format(parsedDate)
    const formattedWeekday = new Intl.DateTimeFormat('ru-RU', {
      weekday: 'long'
    }).format(parsedDate)

    return `${formattedDate}, ${formattedWeekday}`
  }

  private formatSlotDateTime(
    date: string,
    time: string,
    fallback: string
  ): string {
    if (!date || !time) return fallback

    const parsedDate = new Date(`${date}T${time}`)
    if (Number.isNaN(parsedDate.getTime())) return fallback

    const formattedDate = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long'
    }).format(parsedDate)
    const formattedWeekday = new Intl.DateTimeFormat('ru-RU', {
      weekday: 'long'
    }).format(parsedDate)
    const formattedTime = new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsedDate)

    return `${formattedDate}, ${formattedWeekday}, ${formattedTime}`
  }

  private getPlayersText(state: BookingState): string {
    if (!state.slot?.tickets) return state.quest?.players_text || ''

    const ticketsCount = state.tickets?.count || 1

    return `${ticketsCount} ${this.pluralize(ticketsCount, [
      'игрок',
      'игрока',
      'игроков'
    ])}`
  }

  private pluralize(count: number, forms: [string, string, string]): string {
    switch (new Intl.PluralRules('ru-RU').select(count)) {
      case 'one':
        return forms[0]
      case 'few':
        return forms[1]
      default:
        return forms[2]
    }
  }

  /**
   * Обновить количество игроков
   * @param {BookingState} state - состояние бронирования
   */
  updatePlayersCount(state: BookingState): void {
    const counterField = this.modal.querySelector(
      '[data-counter-field][data-name="booking-players-count"]'
    ) as HTMLElement | null

    const value = counterField?.querySelector('[data-counter-display]')
    const input = counterField?.querySelector(
      '[data-counter-input]'
    ) as HTMLInputElement | null
    const decrease = this.modal.querySelector(
      '[data-counter-field][data-name="booking-players-count"] [data-counter-decrease]'
    ) as HTMLButtonElement
    const increase = this.modal.querySelector(
      '[data-counter-field][data-name="booking-players-count"] [data-counter-increase]'
    ) as HTMLButtonElement

    const minPlayers = this.stateManager.getMinPlayersCount()
    const maxPlayers = this.stateManager.getMaxPlayersCount()
    const textFieldInput = this.modal.querySelector<HTMLInputElement>(
      '.text-field-counter .text-field[name="booking-players-count"]'
    )
    const textFieldCounter = textFieldInput?.closest(
      '.text-field-counter'
    ) as HTMLElement | null
    const textFieldDecrease = textFieldCounter?.querySelector(
      '[data-counter-decrease]'
    ) as HTMLButtonElement | null
    const textFieldIncrease = textFieldCounter?.querySelector(
      '[data-counter-increase]'
    ) as HTMLButtonElement | null

    if (counterField) {
      counterField.setAttribute('data-min-value', String(minPlayers))
      counterField.setAttribute('data-max-value', String(maxPlayers))
    }

    if (textFieldCounter) {
      textFieldCounter.setAttribute('data-min-value', String(minPlayers))
      textFieldCounter.setAttribute('data-max-value', String(maxPlayers))
    }

    if (value) {
      value.textContent = String(state.playersCount)
    }

    if (input) {
      input.value = String(state.playersCount)
    }

    if (textFieldInput) {
      textFieldInput.min = String(minPlayers)
      textFieldInput.max = String(maxPlayers)
      textFieldInput.value = String(state.playersCount)
    }

    if (decrease) {
      decrease.disabled = state.playersCount <= minPlayers
    }

    if (increase) {
      increase.disabled = state.playersCount >= maxPlayers
    }

    if (textFieldDecrease) {
      textFieldDecrease.disabled = state.playersCount <= minPlayers
    }

    if (textFieldIncrease) {
      textFieldIncrease.disabled = state.playersCount >= maxPlayers
    }
  }

  private updatePlayersCountVisibility(state: BookingState): void {
    const playersCountSlot = this.modal.querySelector(
      '[data-players-count-slot]'
    ) as HTMLElement | null
    const playersCounter = playersCountSlot?.querySelector(
      '.booking-modal__players-count'
    ) as HTMLElement | null

    if (!playersCountSlot) return

    // Счётчик игроков нужен всегда, кроме двух случаев: билетный режим
    // (там свой счётчик) и тарифная сетка по игрокам (там количество
    // выбирается тарифом). Когда цена фиксированная — «По умолчанию» или
    // цена слота, — количество не влияет на сумму, но площадке важно
    // знать, сколько человек придёт.
    const shouldRender = Boolean(
      state.quest &&
      state.slot &&
      !state.slot.tickets &&
      !this.tariffsHavePlayersGrid(state)
    )

    if (!shouldRender) {
      playersCounter?.remove()
      return
    }

    if (playersCounter) return

    const template = playersCountSlot.querySelector(
      '#booking-players-count-template'
    ) as HTMLTemplateElement | null

    if (!template) return

    const clone = template.content.cloneNode(true) as DocumentFragment
    playersCountSlot.appendChild(clone)

    const input = playersCountSlot.querySelector<HTMLInputElement>(
      '.text-field-counter .text-field[name="booking-players-count"]'
    )

    if (input && !input.dataset.textFieldInitialized) {
      input.dataset.textFieldInitialized = 'true'
      new TextField(input)
    }
  }

  private updateTariffs(state: BookingState): void {
    const tariffsContainer = this.modal.querySelector(
      '[data-tariffs]'
    ) as HTMLElement | null

    if (!tariffsContainer) return

    const knownTariffs = this.currentTariffs(state)

    // Единственный тариф без числа игроков — «По умолчанию: 7500 руб.» —
    // не выбор: селектор прячем, а цена берётся из него же ниже. Пустой
    // список блок не прячет: на его месте — «тарифов нет».
    const meaningfulChoice =
      knownTariffs === null ||
      knownTariffs.length !== 1 ||
      Boolean(knownTariffs[0]?.players)

    const tariffsNeeded = this.tariffsNeeded(state)
    const shouldShowTariffs = tariffsNeeded && meaningfulChoice

    tariffsContainer.classList.toggle('is-hidden', !shouldShowTariffs)

    if (tariffsNeeded && !shouldShowTariffs) {
      // Селектора нет, но цена всё равно из тарифа.
      const flat = knownTariffs?.[0]
      const current = this.stateManager.getState().selectedTariff
      if (flat && (!current || current.price !== flat.price)) {
        this.stateManager.setState({ selectedTariff: flat })
        this.updatePrice()
      }
    }

    if (!shouldShowTariffs) return

    const template = tariffsContainer.querySelector(
      '#booking-tariff-template'
    ) as HTMLTemplateElement | null

    if (!template) return

    let tariffsData = this.normalizeTariffs(state.slot?.prices || [])

    const pricesUrl = state.quest?.prices_url?.trim()
    if (pricesUrl) {
      const tariffCacheKey = this.buildTariffsCacheKey(pricesUrl, state)
      const cachedTariffs = this.tariffsCache.get(tariffCacheKey)

      if (
        !cachedTariffs &&
        !this.tariffsLoading.has(tariffCacheKey) &&
        !this.tariffsFailed.has(tariffCacheKey)
      ) {
        this.loadTariffs(pricesUrl, state, tariffCacheKey)
      }

      this.bindTariffsRetry(tariffsContainer)

      if (cachedTariffs) {
        tariffsData = cachedTariffs

        const currentState = this.stateManager.getState()
        if (currentState.slot) {
          const minTariffPrice = cachedTariffs
            .map((tariff) => Number(tariff.price))
            .filter((price) => Number.isFinite(price) && price >= 0)
            .sort((a, b) => a - b)[0]

          this.stateManager.setState({
            slot: {
              ...currentState.slot,
              ...(minTariffPrice === undefined
                ? {}
                : { min_price: minTariffPrice })
            }
          })
        }
      }
    }

    const options = tariffsData.map((tariff) => ({
      value: tariff.value,
      label: tariff.label
    }))

    this.renderTariffsStatus(tariffsContainer, this.tariffsStatus(state))

    if (options.length === 0) return

    let selectNode = tariffsContainer.querySelector(
      '[data-select-field]'
    ) as SelectType | null

    if (!selectNode) {
      const clone = template.content.cloneNode(true) as DocumentFragment
      tariffsContainer.appendChild(clone)

      selectNode = tariffsContainer.querySelector(
        '[data-select-field]'
      ) as SelectType | null
    }

    if (!selectNode) return

    const selectField = selectNode.select || new SelectField(selectNode)
    selectField.setOptions(options)

    // Тариф не привязан к числу игроков: выбранный тариф хранится отдельно
    // в state.selectedTariff, цена берётся из него самого.
    const currentSelection = this.stateManager.getState().selectedTariff
    const initial =
      currentSelection &&
      tariffsData.some((tariff) => tariff.value === currentSelection.value)
        ? currentSelection
        : tariffsData[0]

    selectField.setValue(initial.value)

    if (
      !currentSelection ||
      currentSelection.value !== initial.value ||
      currentSelection.price !== initial.price
    ) {
      this.stateManager.setState({ selectedTariff: initial })
      this.updatePrice()
    }

    if (!selectNode.dataset.tariffBound) {
      selectNode.addEventListener('selectfield:change', (event: Event) => {
        const selectEvent = event as CustomEvent<{
          name: string
          value: string
        }>
        const selectedValue = String(selectEvent.detail?.value || '')
        const currentState = this.stateManager.getState()
        const pricesUrlNow = currentState.quest?.prices_url?.trim()
        const cacheKey = pricesUrlNow
          ? this.buildTariffsCacheKey(pricesUrlNow, currentState)
          : ''
        const currentTariffs = cacheKey
          ? this.tariffsCache.get(cacheKey) || []
          : this.normalizeTariffs(currentState.slot?.prices || [])
        const chosen = currentTariffs.find(
          (tariff) => tariff.value === selectedValue
        )

        if (!chosen) return

        this.stateManager.setState({ selectedTariff: chosen })
        this.updatePrice()
      })

      selectNode.dataset.tariffBound = 'true'
    }
  }

  /**
   * Загруженные тарифы для текущего слота.
   *
   * @param {BookingState} state - Состояние бронирования.
   * @return {TariffPrice[] | null} Тарифы, либо null, пока они не загружены.
   */
  private currentTariffs(state: BookingState): TariffPrice[] | null {
    const pricesUrl = state.quest?.prices_url?.trim()

    if (!pricesUrl) {
      return this.normalizeTariffs(state.slot?.prices || [])
    }

    const cached = this.tariffsCache.get(
      this.buildTariffsCacheKey(pricesUrl, state)
    )

    return cached || null
  }

  /**
   * Есть ли у слота тарифная сетка по числу игроков.
   *
   * @param {BookingState} state - Состояние бронирования.
   * @return {boolean} true, когда количество игроков выбирается тарифом.
   */
  private tariffsHavePlayersGrid(state: BookingState): boolean {
    const tariffs = this.currentTariffs(state)

    if (tariffs === null || tariffs.length === 0) {
      // Тарифы ещё грузятся, не загрузились или их нет: полагаемся на прежнее
      // правило — при tariff_url сетка вероятна, счётчик не показываем, чтобы
      // не мигал (и без тарифа бронь всё равно не пройдёт).
      return Boolean(state.quest?.prices_url)
    }

    return tariffs.some((tariff) => tariff.players > 0)
  }

  /**
   * Нужны ли слоту тарифы партнёра (tariff_url). Не нужны билетному слоту и
   * билетной системе (Quest#ticket_system): её заказ сервер собирает из
   * ticket_tariffs (Api::BookingController#create_ticket_system_order), а
   * модалка открывается без даты — тарифы на «сейчас» её только блокировали бы.
   *
   * @param {BookingState} state - Состояние бронирования.
   * @return {boolean} true, когда тариф выбирается из ответа партнёра.
   */
  private tariffsNeeded(state: BookingState): boolean {
    return Boolean(
      state.quest?.prices_url?.trim() &&
      !state.slot?.tickets &&
      !state.quest?.ticket_system
    )
  }

  /**
   * Состояние тарифов текущего слота.
   *
   * @param {BookingState} state - Состояние бронирования.
   * @return {TariffsStatus} none, когда тарифы слоту не нужны.
   */
  tariffsStatus(state: BookingState): TariffsStatus {
    const pricesUrl = state.quest?.prices_url?.trim()
    if (!pricesUrl || !this.tariffsNeeded(state)) return 'none'

    const key = this.buildTariffsCacheKey(pricesUrl, state)
    const cached = this.tariffsCache.get(key)
    if (cached) return cached.length > 0 ? 'ready' : 'empty'

    if (this.tariffsFailed.has(key)) return 'failed'

    return this.tariffsSlow.has(key) ? 'slow' : 'loading'
  }

  /**
   * Можно ли идти дальше: без загруженного тарифа сервер отклонит бронь
   * («Выберите тариф»).
   *
   * @param {BookingState} state - Состояние бронирования.
   * @return {boolean} true, когда тарифы не нужны или загружены.
   */
  tariffsAllowSubmit(state: BookingState): boolean {
    const status = this.tariffsStatus(state)
    return status === 'none' || status === 'ready'
  }

  /**
   * Загрузить тарифы слота. Сервер отвечает 503, когда партнёр не отдал
   * тарифы, — тогда один повтор с retry (сервер ждёт партнёра дольше, см.
   * Bookings::TariffProxy#fetch_slow!), затем «Не удалось загрузить» с
   * «Повторить». В кэш попадают только ответы сервера, ошибка — нет.
   *
   * @param {string} pricesUrl - URL тарифов.
   * @param {BookingState} state - Состояние, для которого грузим.
   * @param {string} key - Ключ кэша тарифов.
   * @param {number} attempt - Номер попытки, с нуля.
   */
  private loadTariffs(
    pricesUrl: string,
    state: BookingState,
    key: string,
    attempt = 0
  ): void {
    this.tariffsLoading.add(key)

    void this.fetchTariffs(pricesUrl, state, attempt > 0)
      .then((fetchedTariffs) => {
        this.tariffsCache.set(key, fetchedTariffs)
        this.tariffsLoading.delete(key)
        this.tariffsSlow.delete(key)
        this.updateTariffs(this.stateManager.getState())
        // Тарифы могли оказаться без сетки — тогда нужен счётчик игроков.
        this.updatePlayersCountVisibility(this.stateManager.getState())
        this.updatePlayersCount(this.stateManager.getState())
        this.notifyTariffsChanged()
      })
      .catch((error) => {
        const currentState = this.stateManager.getState()
        const currentUrl = currentState.quest?.prices_url?.trim()
        const isCurrent = Boolean(
          currentUrl &&
          this.buildTariffsCacheKey(currentUrl, currentState) === key
        )

        if (error instanceof TariffsUnavailableError && attempt === 0) {
          this.tariffsSlow.add(key)
          if (isCurrent) this.updateTariffs(currentState)
          setTimeout(
            () => this.loadTariffs(pricesUrl, state, key, attempt + 1),
            TARIFFS_RETRY_DELAY
          )
          return
        }

        this.tariffsLoading.delete(key)
        this.tariffsSlow.delete(key)
        this.tariffsFailed.add(key)

        if (isCurrent) {
          // Сбой партнёра виден на месте тарифов; прочие ошибки (сессия,
          // ответ сервера) — как раньше, в общем блоке ошибок.
          if (!(error instanceof TariffsUnavailableError)) {
            new BookingValidator(this.modal).showError(
              error instanceof Error
                ? error.message
                : 'Не удалось загрузить тарифы. Обновите страницу и попробуйте ещё раз.'
            )
          }
          this.updateTariffs(currentState)
        }
        this.notifyTariffsChanged()
      })
  }

  /**
   * «Повторить» после неудачной загрузки тарифов.
   *
   * @param {HTMLElement} container - Блок тарифов.
   */
  private bindTariffsRetry(container: HTMLElement): void {
    const retry = container.querySelector<HTMLElement>('[data-tariffs-retry]')
    if (!retry || retry.dataset.tariffsRetryBound) return

    retry.addEventListener('click', () => {
      const state = this.stateManager.getState()
      const pricesUrl = state.quest?.prices_url?.trim()
      if (!pricesUrl) return

      this.tariffsFailed.delete(this.buildTariffsCacheKey(pricesUrl, state))
      this.updateTariffs(state)
      this.notifyTariffsChanged()
    })
    retry.dataset.tariffsRetryBound = 'true'
  }

  /**
   * Плашка на месте селектора, пока тарифов для выбора нет.
   *
   * @param {HTMLElement} container - Блок тарифов.
   * @param {TariffsStatus} status - Состояние тарифов слота.
   */
  private renderTariffsStatus(
    container: HTMLElement,
    status: TariffsStatus
  ): void {
    const message = TARIFFS_STATUS_TEXT[status]
    const statusNode = container.querySelector<HTMLElement>(
      '[data-tariffs-status]'
    )
    const text = statusNode?.querySelector<HTMLElement>(
      '[data-tariffs-status-text]'
    )
    const retry = statusNode?.querySelector<HTMLElement>('[data-tariffs-retry]')
    const selectField = container
      .querySelector<HTMLElement>('[data-select-field]')
      ?.closest<HTMLElement>('[data-field]')

    statusNode?.classList.toggle('is-hidden', !message)
    if (text) text.textContent = message || ''
    retry?.classList.toggle('is-hidden', status !== 'failed')
    selectField?.classList.toggle('is-hidden', Boolean(message))
  }

  // Кнопка шага 1 пересчитывает доступность (BookingEventHandlers).
  private notifyTariffsChanged(): void {
    this.modal.dispatchEvent(new CustomEvent('booking:tariffs-changed'))
  }

  // Квест — в ключе: на странице расписания у квестов один prices_url, а
  // тарифы у каждого свои.
  private buildTariffsCacheKey(pricesUrl: string, state: BookingState): string {
    return [
      pricesUrl,
      state.quest?.quest_id || '',
      state.slot?.date || '',
      state.slot?.time || ''
    ].join('|')
  }

  private updateTickets(state: BookingState): void {
    const ticketsContainer = this.modal.querySelector(
      '[data-tickets]'
    ) as HTMLElement | null

    if (!ticketsContainer) return

    const tickets = state.slot?.tickets
    const shouldShowTickets = Boolean(tickets)

    ticketsContainer.classList.toggle('is-hidden', !shouldShowTickets)

    if (!shouldShowTickets || !tickets) {
      this.ticketsCounter = null
      this.stateManager.setState({ tickets: null })
      return
    }

    const template = ticketsContainer.querySelector(
      '#booking-tickets-template'
    ) as HTMLTemplateElement | null
    if (!template) return

    Array.from(ticketsContainer.children).forEach((child) => {
      if (!(child instanceof HTMLTemplateElement)) {
        child.remove()
      }
    })

    const clone = template.content.cloneNode(true) as DocumentFragment
    ticketsContainer.appendChild(clone)

    const minCount = 1
    const maxCount = Math.max(1, Number(tickets.available) || 1)
    const currentCount = state.tickets?.count || minCount
    const ticketsValue = Math.min(maxCount, Math.max(minCount, currentCount))

    const ticketsCount = ticketsContainer.querySelector(
      '[data-tickets-count]'
    ) as HTMLElement | null
    if (ticketsCount) {
      ticketsCount.textContent = `Свободно ${tickets.available || 0} из ${tickets.max_availability || 0} мест`
    }

    const ticketsInput = ticketsContainer.querySelector<HTMLInputElement>(
      '.text-field-counter .text-field[name="booking-tickets-count"]'
    )
    if (!ticketsInput) return

    const ticketsInputCounter = ticketsInput.closest(
      '.text-field-counter'
    ) as HTMLElement | null

    ticketsInput.min = String(minCount)
    ticketsInput.max = String(maxCount)
    ticketsInput.value = String(ticketsValue)

    if (ticketsInputCounter) {
      ticketsInputCounter.dataset.minValue = String(minCount)
      ticketsInputCounter.dataset.maxValue = String(maxCount)
    }

    if (!ticketsInput.dataset.textFieldInitialized) {
      ticketsInput.dataset.textFieldInitialized = 'true'
      new TextField(ticketsInput)
    }

    this.stateManager.setState({
      tickets: {
        count: ticketsValue
      }
    })

    const existingDisclaimer = ticketsContainer.querySelector(
      '.booking-modal__disclaimer'
    ) as HTMLElement | null
    const description = (tickets.description || '').trim()
    if (!description) {
      const partlyBooked =
        Number(tickets.available) < Number(tickets.max_availability)
      if (!partlyBooked) existingDisclaimer?.remove()
      return
    }

    const ticketsDescription = ticketsContainer.querySelector(
      '[data-tickets-description]'
    ) as HTMLElement | null

    if (ticketsDescription) {
      ticketsDescription.innerHTML = description
    }
  }

  private async fetchTariffs(
    url: string,
    state: BookingState,
    slow: boolean
  ): Promise<TariffPrice[]> {
    const { response, data } = await postJson<
      Array<{
        title?: string
        price?: number | string
        players?: number | string
      }>
    >(url, {
      date: state.slot?.date || null,
      time: state.slot?.time || null,
      quest_id: state.quest?.quest_id,
      ...(slow ? { retry: true } : {})
    })

    if (response.status === 503) {
      throw new TariffsUnavailableError('Не удалось загрузить тарифы')
    }

    if (!response.ok) {
      throw new Error('Ошибка загрузки тарифов')
    }

    return data
      .map((item) => {
        const label = String(item.title ?? item.players ?? '')
        return {
          value: label,
          label,
          price: Number(item.price),
          players: Number(item.players) || 0
        }
      })
      .filter(
        (tariff): tariff is TariffPrice =>
          Number.isFinite(tariff.price) && tariff.label !== ''
      )
  }

  private normalizeTariffs(
    prices: Array<{
      players?: number | string
      players_count?: number | string
      price?: number | string
      label?: string
    }>
  ): TariffPrice[] {
    return prices
      .map((priceItem) => {
        const price = Number(priceItem.price)
        if (!Number.isFinite(price)) return null

        const label = String(
          priceItem.label || priceItem.players_count || priceItem.players || ''
        )
        if (!label) return null

        return {
          value: label,
          label,
          price,
          players: Number(priceItem.players) || 0
        }
      })
      .filter((tariff): tariff is TariffPrice => Boolean(tariff))
  }

  /**
   * Обновить опции выбора языка
   * @param {BookingState} state - состояние бронирования
   */
  private updateLanguageOptions(state: BookingState): void {
    if (!state.quest) return

    const languageWrapper = this.modal.querySelector(
      '[data-language-wrapper]'
    ) as HTMLElement
    const optionsContainer = this.modal.querySelector('[data-language-options]')

    const availableLanguages = state.quest.available_languages || []

    if (availableLanguages.length > 1) {
      if (languageWrapper) languageWrapper.style.display = 'block'

      if (optionsContainer) {
        optionsContainer.innerHTML = ''

        availableLanguages.forEach((lang) => {
          const label = document.createElement('label')
          label.innerHTML = `
            <input type="radio"
                   name="booking-language"
                   value="${lang.code}"
                   ${lang.code === state.language ? 'checked' : ''}>
            <span>${lang.name}</span>
          `

          label.querySelector('input')?.addEventListener('change', (e) => {
            this.stateManager.setState({
              language: (e.target as HTMLInputElement).value
            })
          })

          optionsContainer.appendChild(label)
        })
      }
    } else {
      if (languageWrapper) languageWrapper.style.display = 'none'
    }
  }

  /**
   * Обновить способы оплаты
   * @param {BookingState} state - состояние бронирования
   */
  private updatePaymentMethods(state: BookingState): void {
    if (!state.slot) return

    const paymentSection = this.modal.querySelector(
      '[data-payment-section]'
    ) as HTMLElement | null
    const paymentDisclaimer = paymentSection?.querySelector(
      '[data-payment-disclaimer]'
    ) as HTMLElement | null
    const paymentDescription = paymentSection?.querySelector(
      '[data-payment-description]'
    ) as HTMLElement | null
    const timingContainer = this.modal.querySelector(
      '[data-payment-list-when-pay]'
    ) as HTMLElement | null
    const timingTemplate = timingContainer?.querySelector(
      '#booking-payment-template-when-pay'
    ) as HTMLTemplateElement | null
    const paymentMethodsSection = this.modal.querySelector(
      '[data-payment-methods-section]'
    ) as HTMLElement | null
    const optionsContainer = this.modal.querySelector(
      '[data-payment-list]'
    ) as HTMLElement | null
    const template = optionsContainer?.querySelector(
      '#booking-payment-template'
    ) as HTMLTemplateElement | null

    if (
      !paymentSection ||
      !timingContainer ||
      !timingTemplate ||
      !optionsContainer ||
      !template
    )
      return

    let paymentDescriptionText = ''
    const isPaymentStep = state.step === 3
    const prepayRequired = state.slot?.prepay?.prepay_required
    const hasRequiredSlotPrepay =
      prepayRequired === true || prepayRequired === 'true'
    const availablePaymentTimings = resolveAvailablePaymentTimings(
      state,
      hasRequiredSlotPrepay
    )
    const selectedPaymentTiming = availablePaymentTimings.includes(
      state.paymentTiming
    )
      ? state.paymentTiming
      : availablePaymentTimings[0]

    if (selectedPaymentTiming !== state.paymentTiming) {
      this.stateManager.setState({
        paymentTiming: selectedPaymentTiming,
        partial: selectedPaymentTiming === 'prepay'
      })
    }

    if (
      isPaymentStep &&
      (hasRequiredSlotPrepay ||
        state.prepayMode === 'required' ||
        state.prepayMode === 'partial_required')
    ) {
      paymentDescriptionText =
        'До внесения предоплаты ваша бронь не подтверждена.'
    } else if (isPaymentStep && state.prepayMode === 'full_required') {
      paymentDescriptionText =
        'Ожидаем предоплату. Организатор может отменить бронирование, если предоплата не будет внесена.'
    }

    if (paymentDescription) {
      paymentDescription.textContent = paymentDescriptionText
    }

    if (paymentDisclaimer) {
      paymentDisclaimer.classList.toggle(
        'is-hidden',
        paymentDescriptionText.length === 0
      )
    }

    // При prepay: 'none' у квеста нет предоплаты, но клиент всё равно может
    // выбрать между "Оплата на месте" и "Оплатить всю сумму" — секцию не прячем,
    // только убираем из списка вариант "Внести предоплату" (см. timingOptions ниже).
    const shouldHidePaymentSection = !isPaymentStep

    paymentSection.classList.toggle('is-hidden', shouldHidePaymentSection)

    if (shouldHidePaymentSection) {
      if (paymentMethodsSection) {
        paymentMethodsSection.hidden = true
      }

      Array.from(timingContainer.children).forEach((child) => {
        if (!(child instanceof HTMLTemplateElement)) {
          child.remove()
        }
      })
      Array.from(optionsContainer.children).forEach((child) => {
        if (!(child instanceof HTMLTemplateElement)) {
          child.remove()
        }
      })
      return
    }

    Array.from(timingContainer.children).forEach((child) => {
      if (!(child instanceof HTMLTemplateElement)) {
        child.remove()
      }
    })

    Array.from(optionsContainer.children).forEach((child) => {
      if (!(child instanceof HTMLTemplateElement)) {
        child.remove()
      }
    })

    const timingOptions = [
      { value: 'later', label: 'Оплата на месте' },
      { value: 'prepay', label: 'Внести предоплату' },
      { value: 'full', label: 'Оплатить всю сумму' }
    ].filter((option) =>
      availablePaymentTimings.includes(
        option.value as 'later' | 'prepay' | 'full'
      )
    )

    timingOptions.forEach((option) => {
      const clone = timingTemplate.content.cloneNode(true) as DocumentFragment
      const input = clone.querySelector(
        'input[name="payment_timing"]'
      ) as HTMLInputElement | null
      const name = clone.querySelector(
        '[data-payment-name]'
      ) as HTMLElement | null

      if (input) {
        input.value = option.value
        input.checked = option.value === selectedPaymentTiming
        input.addEventListener('change', () => {
          this.stateManager.setState({ paymentTiming: option.value })
          this.updatePaymentMethods(this.stateManager.getState())
          this.updatePrice()
        })
      }

      if (name) {
        name.textContent = option.label
      }

      timingContainer.appendChild(clone)
    })

    const shouldShowPaymentMethods =
      selectedPaymentTiming === 'prepay' || selectedPaymentTiming === 'full'
    if (paymentMethodsSection) {
      paymentMethodsSection.hidden = !shouldShowPaymentMethods
    }

    paymentMethodsSection?.classList.toggle(
      'is-hidden',
      !shouldShowPaymentMethods
    )

    if (!shouldShowPaymentMethods) {
      return
    }

    const paymentMethods = state.paymentMethods || []

    paymentMethods.forEach((method) => {
      const clone = template.content.cloneNode(true) as DocumentFragment
      const input = clone.querySelector(
        'input[name="payment_method"]'
      ) as HTMLInputElement | null
      const image = clone.querySelector(
        '[data-payment-image]'
      ) as HTMLImageElement | null
      const name = clone.querySelector(
        '[data-payment-name]'
      ) as HTMLElement | null

      if (input) {
        input.value = method.name
        input.checked = method.name === state.paymentType
        input.addEventListener('change', () => {
          this.stateManager.setState({ paymentType: method.name })
          this.updatePaymentMethods(this.stateManager.getState())
        })
      }

      if (image) {
        image.src = method.icon || ''
        image.alt = method.icon ? method.title : ''
        image.style.display = method.icon ? '' : 'none'
      }

      if (name) {
        name.textContent = method.title
      }

      optionsContainer.appendChild(clone)
    })
  }

  /**
   * Промокод и сертификат — один блок на всю модалку (part/_discounts): на шаге 2,
   * если у квеста есть доп.услуги, иначе на шаге 1 (шаг 2 тогда не показывается).
   * Узел переносится целиком, поэтому слушатели полей и аккордеон сохраняются.
   * @param {BookingState} state - состояние бронирования
   */
  private placeDiscounts(state: BookingState): void {
    const block = this.modal.querySelector<HTMLElement>(
      '[data-booking-discounts]'
    )
    if (!block) return

    const slot = this.modal.querySelector<HTMLElement>(
      `[data-discounts-slot="${hasServicesStep(state) ? 2 : 1}"]`
    )
    if (slot && block.parentElement !== slot) {
      slot.appendChild(block)
    }
  }

  /**
   * Кнопка шага 1: «Далее», если впереди шаг с услугами, иначе «Забронировать».
   * @param {BookingState} state - состояние бронирования
   */
  private updateStepOneButton(state: BookingState): void {
    const text = this.modal.querySelector<HTMLElement>(
      '[data-step-one-submit] .button__text'
    )
    if (text) {
      text.textContent = hasServicesStep(state) ? 'Далее' : 'Забронировать'
    }
  }

  /**
   * Обновить дополнительные услуги
   * @param {BookingState} state - состояние бронирования
   */
  private updateAdditionalServices(state: BookingState): void {
    const sections = this.modal.querySelectorAll(
      '[data-additional-services]'
    ) as NodeListOf<HTMLElement>
    const servicesCatalog = getBookingServicesCatalog(state)

    sections.forEach((section) => {
      section.hidden = servicesCatalog.length === 0
    })
    if (servicesCatalog.length === 0) return

    sections.forEach((section) => {
      const listContainer = section.querySelector(
        '[data-services-list]'
      ) as HTMLElement | null
      const toggleButton = section.querySelector(
        '[data-services-toggle]'
      ) as HTMLElement | null
      const stepContainer = section.closest('[data-booking-step]')
      const template = stepContainer?.querySelector(
        '[data-booking-service-template]'
      ) as HTMLTemplateElement | null

      if (!listContainer || !template) return

      listContainer.innerHTML = ''

      const visibleServices = servicesCatalog.filter(
        (service) => !this.isHiddenService(service)
      )
      const hiddenServices = servicesCatalog.filter((service) =>
        this.isHiddenService(service)
      )

      visibleServices.forEach((service) => {
        const clone = template.content.cloneNode(true) as DocumentFragment

        this.renderService(clone, service, state)

        listContainer.appendChild(clone)
      })

      if (toggleButton) {
        listContainer.appendChild(toggleButton)

        toggleButton.style.display = hiddenServices.length > 0 ? 'flex' : 'none'
        toggleButton.classList.remove('open')

        const toggleText = toggleButton.querySelector('[data-toggle-text]')
        if (toggleText) {
          toggleText.textContent = 'Показать ещё'
        }
      }

      hiddenServices.forEach((service) => {
        const clone = template.content.cloneNode(true) as DocumentFragment

        this.renderService(clone, service, state)

        listContainer.appendChild(clone)
      })

      listContainer
        .querySelectorAll<HTMLElement>('[data-service]')
        .forEach((item) => {
          const serviceId = Number(item.dataset.serviceId)
          const service = servicesCatalog.find(
            (entry) => Number(entry.service_id) === serviceId
          )
          const isHiddenService = Boolean(
            service && this.isHiddenService(service)
          )

          item.dataset.serviceExtra = isHiddenService ? 'true' : 'false'
          item.style.display = isHiddenService ? 'none' : ''
        })

      listContainer.style.display = ''
      this.updateServicesCount(section, state)
    })
  }

  private updateServicesCount(section: HTMLElement, state: BookingState): void {
    const countWrapper = section.querySelector(
      '[data-services-count]'
    ) as HTMLElement | null
    const valueElement = section.querySelector(
      '[data-services-count-value]'
    ) as HTMLElement | null
    const maxElement = section.querySelector(
      '[data-services-count-total]'
    ) as HTMLElement | null

    if (!countWrapper || !valueElement || !maxElement) return

    const servicesCatalog = getBookingServicesCatalog(state)
    const selectedCount = Object.keys(state.services).filter((serviceId) => {
      const count = Number(state.services[Number(serviceId)])

      return Number.isFinite(count) && count > 0
    }).length
    const maxCount = servicesCatalog.length

    valueElement.textContent = String(selectedCount)
    maxElement.textContent = String(maxCount)
    countWrapper.classList.toggle('is-hidden', maxCount <= 0)
  }

  private isHiddenService(service: BookingService): boolean {
    if (service.is_required) return false

    return service.is_hidden === true || service.is_hidden === 'true'
  }

  /**
   * Отрендерить одну услугу
   * @param {DocumentFragment} clone - клон шаблона
   * @param {BookingService} service - услуга
   * @param {BookingState} state - состояние бронирования
   */
  private renderService(
    clone: DocumentFragment,
    service: BookingService,
    state: BookingState
  ): void {
    const checkbox = clone.querySelector(
      '[data-checkbox-input]'
    ) as HTMLInputElement | null

    const counterField = clone.querySelector(
      '.booking-modal__service-counter'
    ) as HTMLElement | null
    const counterInput = counterField?.querySelector(
      '.text-field-counter .text-field'
    ) as HTMLInputElement | null
    const counterInputWrapper = counterInput?.closest(
      '.text-field-counter'
    ) as HTMLElement | null

    const nameEl = clone.querySelector('[data-service-name]')
    const priceEl = clone.querySelector<HTMLElement>('[data-service-price]')
    const descEl = clone.querySelector('[data-service-description]')
    const paymentEl = clone.querySelector('[data-service-payment]')
    const serviceText = clone.querySelector(
      '.booking-modal__service-text, .booking-service-text'
    ) as HTMLElement | null

    const serviceId = Number(service.service_id)
    if (Number.isNaN(serviceId)) return

    const serviceItem = clone.querySelector(
      '[data-service]'
    ) as HTMLElement | null
    if (serviceItem) {
      serviceItem.dataset.serviceId = String(serviceId)
    }

    const counterName = `service-${serviceId}-number`
    const selectedCount = Number(state.services[serviceId] || 0)
    const shouldRemoveCounter = service.max <= 1
    const shouldInitCounter = service.max > 1
    const minCount = Math.max(1, Number(service.min) || 1)
    const maxCount = Math.max(minCount, Number(service.max) || minCount)
    const initialCount =
      selectedCount > 0
        ? Math.min(maxCount, Math.max(minCount, selectedCount))
        : minCount

    const setCounterVisibility = (isVisible: boolean): void => {
      if (!counterField) return
      counterField.style.display = isVisible ? '' : 'none'
    }

    if (nameEl) nameEl.textContent = service.name
    if (priceEl) {
      const amount = new Intl.NumberFormat('ru-RU').format(service.price)
      this.renderPrice(priceEl, `+ ${amount}`)
    }
    if (descEl) descEl.textContent = service.description || ''
    if (paymentEl) {
      const paymentLabel =
        service.payment_timing === 'with_booking'
          ? 'Оплата при бронировании'
          : 'Оплата на месте'
      paymentEl.textContent = service.is_required
        ? `Обязательная · ${paymentLabel}`
        : paymentLabel
    }

    if (checkbox) {
      checkbox.removeAttribute('name')
      checkbox.required = false
      checkbox.checked = service.is_required === true || selectedCount > 0
      checkbox.disabled = service.is_required === true
    }

    const updateServicesState = (count: number): void => {
      const nextServices = { ...this.stateManager.getState().services }

      if (count > 0) {
        nextServices[serviceId] = count
      } else {
        delete nextServices[serviceId]
      }

      this.stateManager.setState({ services: nextServices })
      this.modal
        .querySelectorAll<HTMLElement>('[data-additional-services]')
        .forEach((section) =>
          this.updateServicesCount(section, this.stateManager.getState())
        )
      this.updatePrice()
      this.modal.dispatchEvent(
        new CustomEvent('booking:services-changed', {
          detail: {
            services: nextServices
          }
        })
      )
    }

    if (shouldRemoveCounter) {
      counterField?.remove()
    } else if (shouldInitCounter && counterField && counterInput) {
      counterInput.name = counterName
      counterInput.required = false
      counterInput.min = String(minCount)
      counterInput.max = String(maxCount)
      counterInput.value = String(initialCount)

      if (counterInputWrapper) {
        counterInputWrapper.dataset.minValue = String(minCount)
        counterInputWrapper.dataset.maxValue = String(maxCount)
      }

      if (!counterInput.dataset.textFieldInitialized) {
        counterInput.dataset.textFieldInitialized = 'true'
        new TextField(counterInput)
      }

      // Счётчик виден сразу (как в макете), а не только после галочки:
      // изменение количества само включает услугу.
      setCounterVisibility(true)

      const onCounterChange = (event: Event): void => {
        const changeEvent = event as CustomEvent<{
          name: string
          value: number
        }>

        if (changeEvent.detail?.name !== counterName) return

        const nextCount = Math.min(
          maxCount,
          Math.max(minCount, Number(changeEvent.detail.value) || minCount)
        )

        if (checkbox && !checkbox.checked && !service.is_required) {
          checkbox.checked = true
        }

        updateServicesState(nextCount)
      }

      counterInputWrapper?.addEventListener('textfield:change', onCounterChange)
    }

    checkbox?.addEventListener('change', () => {
      if (service.is_required) return

      if (!checkbox.checked) {
        updateServicesState(0)
        return
      }

      if (shouldInitCounter) {
        setCounterVisibility(true)
        updateServicesState(Number(counterInput?.value) || minCount)
        return
      }

      updateServicesState(1)
    })

    serviceText?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (target.closest('a, button, input, select, textarea')) return
      if (!checkbox) return
      if (service.is_required) return

      checkbox.checked = !checkbox.checked
      checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  /**
   * Поле «Дата посещения» — только у билетной системы (Quest#ticket_system).
   * Скрытое поле (.is-hidden) проверка формы пропускает. Границы — по
   * локальному «сегодня» клиента; точную проверку по поясу города делает
   * сервер (Quest#ticket_visit_dates).
   * @param {BookingState} state - состояние бронирования
   */
  private updateTicketVisitDate(state: BookingState): void {
    const block = this.modal.querySelector<HTMLElement>(
      '[data-ticket-visit-date]'
    )
    const input = block?.querySelector<HTMLInputElement>(
      '[data-ticket-visit-date-input]'
    )
    if (!block || !input) return

    const isTicketSystem = Boolean(state.quest?.ticket_system)
    block.classList.toggle('is-hidden', !isTicketSystem)
    if (!isTicketSystem) return

    const today = new Date()
    const lastDay = new Date(today)
    lastDay.setDate(
      today.getDate() + (state.quest?.ticket_visit_days_ahead || 365)
    )
    // Значение не трогаем: updateAll идёт и посреди ввода даты по частям и
    // стёр бы недописанное. При закрытии модалки поле очищает form.reset().
    input.min = this.toIsoDate(today)
    input.max = this.toIsoDate(lastDay)
  }

  // YYYY-MM-DD по локальному времени: toISOString сдвинул бы дату в UTC.
  private toIsoDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
  }

  /**
   * Обновить список «Билеты» билетной системы (Quest#ticket_system) — по одному
   * чекбоксу+счётчику на тариф из quest.ticket_system_tariffs, аналогично
   * дополнительным услугам (updateAdditionalServices/renderService), но без
   * даты/времени: тариф выбирается сразу вместе с количеством.
   * @param {BookingState} state - состояние бронирования
   */
  private updateTicketTariffs(state: BookingState): void {
    const sections = this.modal.querySelectorAll(
      '[data-ticket-tariffs]'
    ) as NodeListOf<HTMLElement>
    const tariffs = state.quest?.ticket_system_tariffs || []
    const shouldShow = Boolean(state.quest?.ticket_system) && tariffs.length > 0

    sections.forEach((section) => {
      section.hidden = !shouldShow
    })
    if (!shouldShow) return

    sections.forEach((section) => {
      const listContainer = section.querySelector(
        '[data-ticket-tariffs-list]'
      ) as HTMLElement | null
      const stepContainer = section.closest('[data-booking-step]')
      const template = stepContainer?.querySelector(
        '[data-booking-ticket-tariff-template]'
      ) as HTMLTemplateElement | null

      if (!listContainer || !template) return

      listContainer.innerHTML = ''

      tariffs.forEach((tariff, index) => {
        const clone = template.content.cloneNode(true) as DocumentFragment

        this.renderTicketTariff(clone, tariff, index, state)

        listContainer.appendChild(clone)
      })
    })
  }

  private renderTicketTariff(
    clone: DocumentFragment,
    tariff: TicketTariff,
    index: number,
    state: BookingState
  ): void {
    const checkbox = clone.querySelector(
      '[data-checkbox-input]'
    ) as HTMLInputElement | null
    const counterField = clone.querySelector(
      '.booking-modal__ticket-tariff-counter'
    ) as HTMLElement | null
    const counterInput = counterField?.querySelector(
      '.text-field-counter .text-field'
    ) as HTMLInputElement | null
    const counterInputWrapper = counterInput?.closest(
      '.text-field-counter'
    ) as HTMLElement | null

    const titleEl = clone.querySelector('[data-ticket-tariff-title]')
    const descEl = clone.querySelector('[data-ticket-tariff-description]')
    const item = clone.querySelector(
      '[data-ticket-tariff]'
    ) as HTMLElement | null

    if (item) item.dataset.tariffIndex = String(index)
    if (titleEl) titleEl.textContent = tariff.title
    if (descEl) {
      renderWithCurrency(
        descEl,
        `(Стоимость - ${tariff.price}${this.currencyUnit || '₽'}. ${tariff.priceDescription || ''})`,
        this.currencyUnit || '₽'
      )
    }

    const minCount = 1
    const tariffMax =
      typeof tariff.maxQuantity === 'number' && tariff.maxQuantity > 0
        ? tariff.maxQuantity
        : undefined
    const maxCount = tariffMax ?? state.quest?.max_tickets ?? 99
    const selectedQuantity = Number(state.ticketTariffs[index] || 0)
    const initialCount = selectedQuantity > 0 ? selectedQuantity : minCount
    const counterName = `ticket-tariff-${index}-quantity`

    if (checkbox) {
      checkbox.removeAttribute('name')
      checkbox.required = false
      checkbox.checked = selectedQuantity > 0
    }

    const setCounterVisibility = (isVisible: boolean): void => {
      if (!counterField) return
      counterField.style.display = isVisible ? '' : 'none'
    }

    const updateTariffState = (quantity: number): void => {
      const next = { ...this.stateManager.getState().ticketTariffs }

      if (quantity > 0) {
        next[index] = quantity
      } else {
        delete next[index]
      }

      this.stateManager.setState({ ticketTariffs: next })
      this.updatePrice()
    }

    if (counterField && counterInput) {
      counterInput.name = counterName
      counterInput.required = false
      counterInput.min = String(minCount)
      counterInput.max = String(maxCount)
      counterInput.value = String(initialCount)

      if (counterInputWrapper) {
        counterInputWrapper.dataset.minValue = String(minCount)
        counterInputWrapper.dataset.maxValue = String(maxCount)
      }

      if (!counterInput.dataset.textFieldInitialized) {
        counterInput.dataset.textFieldInitialized = 'true'
        new TextField(counterInput)
      }

      setCounterVisibility(selectedQuantity > 0)

      const onCounterChange = (event: Event): void => {
        const changeEvent = event as CustomEvent<{
          name: string
          value: number
        }>

        if (changeEvent.detail?.name !== counterName) return
        if (!checkbox?.checked) return

        const nextCount = Math.min(
          maxCount,
          Math.max(minCount, Number(changeEvent.detail.value) || minCount)
        )

        updateTariffState(nextCount)
      }

      counterInputWrapper?.addEventListener('textfield:change', onCounterChange)
    }

    checkbox?.addEventListener('change', () => {
      if (!checkbox.checked) {
        updateTariffState(0)
        setCounterVisibility(false)
        return
      }

      setCounterVisibility(true)
      updateTariffState(Number(counterInput?.value) || minCount)
    })
  }

  /**
   * Обновить отображение цены
   */
  updatePrice(): void {
    const state = this.stateManager.getState()
    const bookingBaseTotal = this.stateManager.calculateBookingBaseTotal()
    const localServicesTotal = this.stateManager.calculateServicesTotal()
    const denominator = this.stateManager.perPlayerDenominator()

    const totalElements = this.modal.querySelectorAll(
      '[data-summary-total]'
    ) as NodeListOf<HTMLElement>
    const perPlayerElements = this.modal.querySelectorAll(
      '[data-summary-per-player]'
    ) as NodeListOf<HTMLElement>
    // Сумма без знака валюты: знак добавляет renderPrice отдельным span.
    const formatPrice = (value: number, sign: '+' | '-' | '' = ''): string => {
      const amount = new Intl.NumberFormat('ru-RU').format(
        Math.max(0, Math.round(Math.abs(value)))
      )
      const prefix = sign ? `${sign} ` : ''

      return `${prefix}${amount}`
    }
    // Цена игры до скидок для строк «Сумма заказа» и «за игрока». До брони —
    // локальный расчёт; после брони сервер присылает booking_price (итог с
    // услугами и уже вычтенным промокодом), из него цену игры восстанавливаем:
    // «Сумма заказа 5 000» над строкой «Доп. услуга + 4 000» читалось как 9 000.
    const serverPriceForStep = (
      step: string | null | undefined
    ): number | null =>
      (step === '2' || step === '3') && state.bookingPrice !== null
        ? state.bookingPrice
        : null
    const gamePriceForStep = (step: string | null | undefined): number => {
      const serverPrice = serverPriceForStep(step)
      if (serverPrice === null) return bookingBaseTotal

      // Стоимость услуг берём из того же ответа сервера, что и booking_price;
      // локальный расчёт по каталогу — только если сервер её не прислал.
      const servicesPrice = state.servicesPrice ?? localServicesTotal
      const appliedPromoDiscount = Math.max(
        0,
        Number(state.promoDiscount?.amount ?? 0)
      )

      return Math.max(0, serverPrice - servicesPrice + appliedPromoDiscount)
    }

    totalElements.forEach((totalEl) => {
      const stepContainer = totalEl.closest('[data-booking-step]')
      const step = stepContainer?.getAttribute('data-booking-step')
      const isStepOne = step === '1'
      const isStepTwo = step === '2'
      const isStepThree = step === '3'
      // Итог для оплаты (orderTotal ниже) остаётся серверным.
      const serverPrice = serverPriceForStep(step)
      const displayTotal = gamePriceForStep(step)

      this.renderPrice(totalEl, formatPrice(displayTotal))

      const priceList = totalEl.closest(
        '.booking-modal__price-list'
      ) as HTMLElement | null
      const template = priceList?.querySelector(
        '#booking-price-item-template'
      ) as HTMLTemplateElement | null

      if (!priceList || !template) return

      // Разбивка нужна и при оплате на месте: клиент видит, сколько платить.
      priceList.hidden = false

      priceList
        .querySelectorAll('[data-services-price-item]')
        .forEach((item) => item.remove())

      const servicesCatalog = getBookingServicesCatalog(state)
      const selectedServices = Object.entries(state.services)
        .map(([serviceId, count]) => {
          const service = servicesCatalog.find(
            (entry) => Number(entry.service_id) === Number(serviceId)
          )
          const normalizedCount = Number(count)

          if (!service || !Number.isFinite(normalizedCount)) return null

          return {
            service,
            count: normalizedCount,
            total: Number(service.price || 0) * normalizedCount
          }
        })
        .filter(
          (
            entry
          ): entry is {
            service: BookingService
            count: number
            total: number
          } => Boolean(entry) && entry.count > 0
        )

      if (isStepTwo || isStepThree) {
        selectedServices.forEach(({ service, count, total }) => {
          const clone = template.content.cloneNode(true) as DocumentFragment
          const createdItem = clone.querySelector(
            '.booking-modal__price-item'
          ) as HTMLElement | null

          if (!createdItem) return

          createdItem.setAttribute('data-services-price-item', 'true')
          const servicesLabel = createdItem.querySelector(
            '.booking-modal__price-label'
          ) as HTMLElement | null
          const servicesValue = createdItem.querySelector(
            '.booking-modal__price-value'
          ) as HTMLElement | null

          if (servicesLabel) {
            servicesLabel.textContent =
              count > 1 ? `${service.name} x${count}` : service.name
          }

          if (servicesValue) {
            this.renderPrice(servicesValue, formatPrice(total, '+'))
          }

          priceList.appendChild(clone)
        })
      }

      const prepayOnlyCheckbox = this.modal.querySelector(
        '[data-booking-step="3"] input[name="prepay-only"]'
      ) as HTMLInputElement | null
      const prepayFullCheckbox = this.modal.querySelector(
        '[data-booking-step="3"] input[name="prepay-full"]'
      ) as HTMLInputElement | null
      const prepayOnlyWrapper = this.modal.querySelector(
        '[data-booking-step="3"] [data-prepay-only-wrapper]'
      ) as HTMLElement | null
      const prepayFullWrapper = this.modal.querySelector(
        '[data-booking-step="3"] [data-prepay-full-wrapper]'
      ) as HTMLElement | null
      const stepTwoSubmitButton = this.modal.querySelector(
        '[data-booking-step="2"] button[type="submit"]'
      ) as HTMLButtonElement | null
      const paymentSubmitButton = this.modal.querySelector(
        '[data-booking-step="3"] button[type="submit"]'
      ) as HTMLButtonElement | null

      const upsertPriceItem = (
        key: string,
        label: string,
        value: number
      ): void => {
        let item = priceList.querySelector(
          `[data-price-item-key="${key}"]`
        ) as HTMLElement | null

        if (!item) {
          const clone = template.content.cloneNode(true) as DocumentFragment
          const createdItem = clone.querySelector(
            '.booking-modal__price-item'
          ) as HTMLElement | null

          if (!createdItem) return

          createdItem.setAttribute('data-price-item-key', key)
          priceList.appendChild(clone)

          item = priceList.querySelector(
            `[data-price-item-key="${key}"]`
          ) as HTMLElement | null
        }

        if (!item) return

        const itemLabel = item.querySelector(
          '.booking-modal__price-label'
        ) as HTMLElement | null
        const itemValue = item.querySelector(
          '.booking-modal__price-value'
        ) as HTMLElement | null

        if (itemLabel) itemLabel.textContent = label
        if (itemValue) {
          this.renderPrice(itemValue, formatPrice(value, value < 0 ? '-' : ''))
        }
      }

      const removePriceItem = (key: string): void => {
        priceList.querySelector(`[data-price-item-key="${key}"]`)?.remove()
      }

      removePriceItem('pay-now')
      removePriceItem('pay-later')
      removePriceItem('certificate-discount')
      removePriceItem('promo-discount')
      removePriceItem('order-total')

      const subtotal =
        serverPrice !== null ? serverPrice : displayTotal + localServicesTotal
      const certificateDiscount = Math.max(
        0,
        Number(state.certificateDiscount || 0)
      )
      // Если промокод уже применён сервером к брони (amount задан), скидка уже
      // учтена в subtotal — для "Итого" её вычитать повторно не нужно, но сумму
      // для информационной строки "Промокод" всё равно показываем.
      // До бронирования скидка считается от цены игры (bookingBaseTotal), а не
      // от суммы с услугами — как на сервере.
      const promoDiscountApplied = state.promoDiscount?.amount !== undefined
      const promoDiscountDisplay = state.promoDiscount
        ? Math.max(
            0,
            state.promoDiscount.amount ??
              this.stateManager.calculatePromoDiscount(bookingBaseTotal)
          )
        : 0
      const promoDiscount = promoDiscountApplied ? 0 : promoDiscountDisplay
      const orderTotal = Math.max(
        0,
        subtotal - certificateDiscount - promoDiscount
      )

      // Строки скидок нужны и на шаге 1: у квестов без доп.услуг промокод и
      // сертификат вводятся там (см. placeDiscounts).
      const hasDiscountLines =
        certificateDiscount > 0 || promoDiscountDisplay > 0
      if (isStepOne || isStepTwo || isStepThree) {
        if (certificateDiscount > 0) {
          upsertPriceItem(
            'certificate-discount',
            'Сертификат',
            -certificateDiscount
          )
        }

        if (promoDiscountDisplay > 0) {
          upsertPriceItem('promo-discount', 'Промокод', -promoDiscountDisplay)
        }
      }

      if (isStepOne && hasDiscountLines) {
        upsertPriceItem('order-total', 'Итого', orderTotal)
      }

      if (isStepTwo) {
        upsertPriceItem('order-total', 'Итого', orderTotal)

        prepayOnlyWrapper?.classList.add('is-hidden')
        prepayFullWrapper?.classList.add('is-hidden')

        const submitButtonText =
          stepTwoSubmitButton?.querySelector('.button__text')

        if (submitButtonText) {
          submitButtonText.textContent = 'Забронировать'
        } else if (stepTwoSubmitButton) {
          stepTwoSubmitButton.textContent = 'Забронировать'
        }
      } else if (isStepThree) {
        const prepayAmount = Math.max(0, Number(state.prepayAmount || 0))
        const onlinePaymentTotal =
          state.prepayAmount === null
            ? orderTotal
            : Math.min(orderTotal, prepayAmount)
        const fullPaymentAmount = resolveFullPaymentAmount(state, orderTotal)
        const onlyChecked = Boolean(prepayOnlyCheckbox?.checked)
        const fullChecked = Boolean(prepayFullCheckbox?.checked)
        const timing = state.paymentTiming
        const setPaymentButtonText = (text: string): void => {
          const submitButtonText =
            paymentSubmitButton?.querySelector('.button__text')

          if (submitButtonText) {
            submitButtonText.textContent = text
          } else if (paymentSubmitButton) {
            paymentSubmitButton.textContent = text
          }
        }

        if (timing === 'later') {
          // Оплата на месте: вся сумма (с услугами и скидками) на месте,
          // онлайн ничего не списывается — кнопка просто закрывает окно.
          upsertPriceItem('pay-later', 'К оплате на месте', orderTotal)
          prepayOnlyWrapper?.classList.add('is-hidden')
          prepayFullWrapper?.classList.add('is-hidden')
          this.stateManager.setState({ partial: false })
          setPaymentButtonText('Закрыть')
        } else if (timing === 'prepay') {
          const payNow = prepayAmount
          const payLater = Math.max(0, orderTotal - payNow)

          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате сейчас (предоплата)', payNow)

          prepayOnlyWrapper?.classList.add('is-hidden')
          prepayFullWrapper?.classList.add('is-hidden')
          this.stateManager.setState({ partial: true })
          setPaymentButtonText('Оплатить')
        } else if (timing === 'full') {
          const payLater = Math.max(0, orderTotal - fullPaymentAmount)
          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате сейчас', fullPaymentAmount)

          prepayOnlyWrapper?.classList.add('is-hidden')
          prepayFullWrapper?.classList.add('is-hidden')
          this.stateManager.setState({
            partial: fullPaymentAmount < orderTotal
          })
          setPaymentButtonText('Оплатить')
        } else if (state.prepayMode === 'none') {
          upsertPriceItem('pay-later', 'К оплате на месте', orderTotal)
          prepayOnlyWrapper?.classList.add('is-hidden')
          prepayFullWrapper?.classList.add('is-hidden')
          this.stateManager.setState({ partial: false })
          setPaymentButtonText('Отправить')
        } else if (
          state.prepayMode === 'optional' ||
          state.prepayMode === 'optional_partial'
        ) {
          if (
            state.partial &&
            !onlyChecked &&
            !fullChecked &&
            prepayOnlyCheckbox
          ) {
            prepayOnlyCheckbox.checked = true
          }

          const resolvedOnlyChecked = Boolean(prepayOnlyCheckbox?.checked)
          const resolvedFullChecked = Boolean(prepayFullCheckbox?.checked)
          let payNow = 0

          if (resolvedFullChecked) {
            payNow = onlinePaymentTotal
          } else if (resolvedOnlyChecked) {
            payNow = prepayAmount
          }

          const payLater = Math.max(0, orderTotal - payNow)

          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате сейчас', payNow)

          prepayOnlyWrapper?.classList.remove('is-hidden')
          if (state.fullOnlinePaymentDisabled) {
            prepayFullWrapper?.classList.add('is-hidden')
            if (prepayFullCheckbox) prepayFullCheckbox.checked = false
          } else {
            prepayFullWrapper?.classList.remove('is-hidden')
          }
          this.stateManager.setState({ partial: resolvedOnlyChecked })
          setPaymentButtonText(payNow > 0 ? 'Оплатить' : 'Подтвердить')

          if (resolvedOnlyChecked && prepayFullCheckbox) {
            prepayFullCheckbox.checked = false
          }

          if (resolvedFullChecked && prepayOnlyCheckbox) {
            prepayOnlyCheckbox.checked = false
          }
        } else if (state.prepayMode === 'optional_full') {
          const payNow = fullChecked ? onlinePaymentTotal : 0
          const payLater = Math.max(0, orderTotal - payNow)

          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате сейчас', payNow)

          prepayOnlyWrapper?.classList.add('is-hidden')
          if (prepayOnlyCheckbox) {
            prepayOnlyCheckbox.checked = false
          }
          if (state.fullOnlinePaymentDisabled) {
            prepayFullWrapper?.classList.add('is-hidden')
            if (prepayFullCheckbox) prepayFullCheckbox.checked = false
          } else {
            prepayFullWrapper?.classList.remove('is-hidden')
          }
          this.stateManager.setState({ partial: false })
          setPaymentButtonText(payNow > 0 ? 'оплатить' : 'подтвердить')
        } else if (
          state.prepayMode === 'required' ||
          state.prepayMode === 'partial_required'
        ) {
          const fullOnlineChecked = Boolean(prepayFullCheckbox?.checked)
          const payNow = fullOnlineChecked ? onlinePaymentTotal : prepayAmount
          const payLater = Math.max(0, orderTotal - payNow)

          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате сейчас', payNow)

          prepayOnlyWrapper?.classList.add('is-hidden')
          if (state.fullOnlinePaymentDisabled) {
            prepayFullWrapper?.classList.add('is-hidden')
            if (prepayFullCheckbox) prepayFullCheckbox.checked = false
          } else {
            prepayFullWrapper?.classList.remove('is-hidden')
          }
          if (prepayOnlyCheckbox) {
            prepayOnlyCheckbox.checked = false
          }
          this.stateManager.setState({ partial: !fullOnlineChecked })
          setPaymentButtonText(payNow > 0 ? 'Оплатить' : 'Подтвердить')
        } else if (state.prepayMode === 'full_required') {
          const payLater = Math.max(0, orderTotal - onlinePaymentTotal)
          upsertPriceItem('pay-later', 'К оплате на месте', payLater)
          upsertPriceItem('pay-now', 'К оплате', onlinePaymentTotal)

          prepayOnlyWrapper?.classList.add('is-hidden')
          prepayFullWrapper?.classList.add('is-hidden')
          if (prepayOnlyCheckbox) {
            prepayOnlyCheckbox.checked = false
          }

          if (prepayFullCheckbox) {
            prepayFullCheckbox.checked = false
          }
          this.stateManager.setState({
            partial: onlinePaymentTotal < orderTotal
          })
          setPaymentButtonText(orderTotal > 0 ? 'Оплатить' : 'Подтвердить')
        }
      } else {
        prepayOnlyWrapper?.classList.add('is-hidden')
        prepayFullWrapper?.classList.add('is-hidden')
      }
    })

    // Цена «за игрока» показывается в режиме билетов и в режиме тарифов (quest.prices_url,
    // т.е. у интеграции задан tariff_url) — во втором случае только когда выбранный тариф знает
    // своё число игроков. Без tariff_url счётчик игроков виден и управляем
    // клиентом, но карточка «за игрока» для таких квестов не показывается.
    const isTariffMode = Boolean(
      state.quest?.prices_url?.trim() && state.slot && !state.slot.tickets
    )
    const hasKnownTariffPlayers = Boolean(
      state.selectedTariff?.players && state.selectedTariff.players > 0
    )
    const isTicketsMode = Boolean(state.slot?.tickets)
    const showPerPlayer =
      (isTariffMode && hasKnownTariffPlayers) || isTicketsMode

    perPlayerElements.forEach((perPlayerEl) => {
      if (!showPerPlayer) {
        perPlayerEl.classList.add('is-hidden')
        return
      }
      perPlayerEl.classList.remove('is-hidden')

      const stepContainer = perPlayerEl.closest('[data-booking-step]')
      const step = stepContainer?.getAttribute('data-booking-step')
      // Та же цена игры, что в «Сумме заказа»: без услуг и скидок.
      const displayPerPlayer = Math.floor(
        gamePriceForStep(step) / Math.max(1, denominator)
      )

      this.renderPrice(
        perPlayerEl,
        formatPrice(displayPerPlayer),
        isTicketsMode ? '' : '~ ',
        ' за игрока'
      )
    })
  }

  /**
   * Показать блок с SMS кодом
   */
  showSmsCode(): void {
    const smsCodeBlock = this.modal.querySelector('[data-sms-code]')
    smsCodeBlock?.classList.remove('is-hidden')
    smsCodeBlock?.classList.add('is-shown')

    const smsCodeInput = this.modal.querySelector(
      'input[name="sms_code"]'
    ) as HTMLInputElement | null
    if (smsCodeInput) {
      // Ставим фокус после отрисовки блока
      setTimeout(() => smsCodeInput.focus(), 0)
    }
  }

  /**
   * Скрыть блок с SMS кодом
   */
  hideSmsCode(): void {
    const smsCodeBlock = this.modal.querySelector('[data-sms-code]')
    smsCodeBlock?.classList.remove('is-shown')
    smsCodeBlock?.classList.add('is-hidden')
  }

  /**
   * Показать, что код подтвержден
   */
  showCodeSuccess(): void {
    const codeButton = this.modal.querySelector('[data-code-button]')
    const successIcon = this.modal.querySelector(
      '[data-code-success-icon]'
    ) as HTMLElement

    codeButton?.setAttribute('data-code-status', 'SUCCESS')
    const buttonText = codeButton?.querySelector('[data-code-button-text]')
    if (buttonText) buttonText.textContent = 'подтверждён'
    if (successIcon) successIcon.style.display = 'block'
  }

  /**
   * Обновить кнопку шага 2 (зависит от чекбокса)
   */
  updateStepTwoButton(): void {
    const button = this.modal.querySelector(
      '[data-step-two-submit]'
    ) as HTMLButtonElement
    const agreeCheckbox = this.modal.querySelector(
      '[data-booking-agree]'
    ) as HTMLInputElement

    if (button && agreeCheckbox) {
      button.disabled = !agreeCheckbox.checked
    }
  }
}
